import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  User,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getIdToken,
  signInAnonymously,
  signInWithCustomToken,
  updateProfile,
  sendPasswordResetEmail,
  signInWithCredential,
  UserCredential,
  getAdditionalUserInfo,
  Unsubscribe,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  serverTimestamp,
  Firestore,
  onSnapshot,
} from 'firebase/firestore';
import { openDB } from 'idb';
import { ELoginProvider, emptyUser, IUser } from '@/models';
import { isUserAnonymous } from '../helpers/user';
import { apiUser } from '../api/apiUser';
import { logger } from '@/utils';
import { extensionUtils } from '@/utils';
import { mixpanelUtils } from './mixpanel';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';
const FIREBASE_IDB_NAME = 'firebaseLocalStorageDb';
const FIREBASE_IDB_STORE = 'firebaseLocalStorage';
const USER_CONFIG_SYNC_COLLECTION = 'sync2.0';

// Firebase configuration
// Replace with your own Firebase config
const firebaseConfig = {
  apiKey: 'AIzaSyA7_8shaIYkL-iky-ku0qhEfJ4RRzD7AQE',
  authDomain: 'login.blocksite.co',
  projectId: 'blcoksite-android',
  storageBucket: 'blcoksite-android.appspot.com',
  messagingSenderId: '483008214107',
  appId: '1:483008214107:web:6e527ccb4a0b62fb22fb7e',
};

class FirebaseUtils {
  public app;
  public auth;
  public analytics;
  public firestore: Firestore;

  constructor() {
    // Initialize Firebase
    this.app = initializeApp(firebaseConfig);
    this.analytics = getAnalytics(this.app);
    this.auth = getAuth(this.app);
    this.firestore = getFirestore(this.app);
  }

  public async init(): Promise<User> {
    return new Promise((resolve, reject) => {
      const unsubscribe = this.auth.onAuthStateChanged(async user => {
        unsubscribe();
        const extUser = await extensionUtils.askForExtensionUser();
        if (extUser?.uid) {
          // if user id  is the same as extension - resolve
          if (user && user.uid === extUser?.uid) {
            resolve(user);
            return;
          }
          // if extUser  or user is not the same or no user - restore user
          const restoredUser = await this.trySignInWithExtensionIdToken();
          if (restoredUser) {
            resolve(restoredUser);
            return;
          }
        }
        // if user still exists locally but restore failed, restore user with local user
        if (user) {
          resolve(user);
          return;
        }
        if (extUser?.uid) {
          logger.warn(
            `Extension user exists but no local user and restore failed`,
            {
              tags: {
                feature: LOGGER_FEATURE.FIREBASE,
                action: LOGGER_ACTION.EXTENSION_SYNC,
              },
              extra: { userId: extUser.uid },
            }
          );
        }
        //create a new user if all else fails
        this.initFirebaseAnonymously(true).then(resolve).catch(reject);
      });
    });
  }
  private async restoreSessionFromIdb(data: unknown): Promise<never> {
    const db = await openDB(FIREBASE_IDB_NAME, 1, {
      upgrade: database => {
        if (!database.objectStoreNames.contains(FIREBASE_IDB_STORE)) {
          database.createObjectStore(FIREBASE_IDB_STORE);
        }
      },
    });
    await db.put(FIREBASE_IDB_STORE, data);
    db.close();
    window.location.reload();
    return null as unknown as never;
  }

  private async trySignInWithExtensionIdToken(): Promise<User | null> {
    try {
      const idToken = await extensionUtils.getUserIdTokenFromExtension();
      if (!idToken) {
        return null;
      }

      const customToken = await apiUser.getCustomToken(idToken);
      const { user } = await signInWithCustomToken(this.auth, customToken);
      return user;
    } catch (error) {
      logger.error('Failed to sign in with extension ID token', error, {
        tags: {
          feature: LOGGER_FEATURE.FIREBASE,
          action: LOGGER_ACTION.EXTENSION_SYNC,
        },
      });
      return null;
    }
  }

  /**
   * Subscribe to auth state changes (for cross-tab sync)
   * Firebase automatically syncs auth state across tabs
   */
  public onAuthStateChanged(
    callback: (user: User | null) => void
  ): Unsubscribe {
    return this.auth.onAuthStateChanged(callback);
  }

  /**
   * Login with email and password
   * @param email - User email
   * @param password - User password
   * @returns Firebase User
   */
  public async signInWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<UserCredential> {
    try {
      return await signInWithEmailAndPassword(this.auth, email, password);
    } catch (error) {
      logger.error('Failed to sign in with email and password', error, {
        tags: {
          feature: LOGGER_FEATURE.FIREBASE,
          action: LOGGER_ACTION.EMAIL_LOGIN,
        },
      });
      throw error;
    }
  }

  /**
   * Register with email and password
   * @param email - User email
   * @param password - User password
   * @returns Firebase User
   */
  public async registerWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<UserCredential> {
    try {
      return await createUserWithEmailAndPassword(this.auth, email, password);
    } catch (error) {
      logger.error('Failed to register with email and password', error, {
        tags: {
          feature: LOGGER_FEATURE.FIREBASE,
          action: LOGGER_ACTION.REGISTER,
        },
      });
      throw error;
    }
  }

  /**
   * Logout the current user
   * @returns true if successful
   */
  public async logoutUser(): Promise<boolean> {
    try {
      await signOut(this.auth);
      return true;
    } catch (error) {
      logger.error('Failed to logout user from Firebase', error, {
        tags: {
          feature: LOGGER_FEATURE.FIREBASE,
          action: LOGGER_ACTION.LOGOUT,
        },
      });
      throw error;
    }
  }

  /**
   * Login with Google
   * @returns Firebase User
   */
  public async loginWithProvider(
    providerType: ELoginProvider
  ): Promise<UserCredential> {
    let provider: GoogleAuthProvider | FacebookAuthProvider;
    switch (providerType) {
      case ELoginProvider.google:
        provider = new GoogleAuthProvider();
        break;
      case ELoginProvider.facebook:
        provider = new FacebookAuthProvider();
        provider.addScope('email');
        break;
      case ELoginProvider.apple:
        provider = new OAuthProvider('apple.com');
        break;
      default:
        throw new Error(`Unsupported login provider: ${providerType}`);
    }

    return await signInWithPopup(this.auth, provider);
  }

  public async loginWithGoogleOneTap(
    credential: string
  ): Promise<UserCredential> {
    try {
      const authCredential = GoogleAuthProvider.credential(credential);
      return await signInWithCredential(this.auth, authCredential);
    } catch (error) {
      logger.error('Failed to login with Google One Tap', error, {
        tags: {
          feature: LOGGER_FEATURE.FIREBASE,
          action: LOGGER_ACTION.GOOGLE_ONE_TAP,
        },
      });
      throw error;
    }
  }

  /**
   * return user credentials which can be used to get the additional info
   * @param userCredentials
   */
  public getAdditionalUserInfo(userCredentials: UserCredential) {
    return getAdditionalUserInfo(userCredentials);
  }

  /**
   * Get the current user's authentication token
   * @returns Firebase auth token or null
   */
  public async getUserToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (user) {
      return await getIdToken(user);
    }
    return null;
  }

  /**
   * Get the current user's authentication token
   * @returns Firebase auth token or null
   */
  public getUserId(): string | null {
    const user = this.auth.currentUser;
    if (user) {
      return user.uid;
    }
    return null;
  }

  public async fbUserToUser(fbUser: User | null): Promise<IUser> {
    const user: IUser = fbUser
      ? {
          uid: fbUser.uid,
          email: fbUser.email || '',
          phoneNumber: fbUser.phoneNumber || '',
          displayName: fbUser.displayName || '',
          photoURL: fbUser.photoURL || '',
          isAnonymous: fbUser.isAnonymous ?? true,
          isPremium: false,
        }
      : emptyUser;

    if (isUserAnonymous(user)) {
      user.isPremium = false;
      user.premiumDetails = null;
    } else {
      const premiumDetailsResponse = await apiUser.getPremiumDetails();
      if (premiumDetailsResponse) {
        // extension only: the web app already has the status from the server
        extensionUtils.syncPremiumStatus(premiumDetailsResponse.isPremium);
        user.isPremium = premiumDetailsResponse.isPremium;
        if (user.isPremium) {
          user.premiumDetails = premiumDetailsResponse.premiumDetails;
        }
      }
    }

    // Update Mixpanel user tier - this is the source of truth for premium status on login/init
    mixpanelUtils.setUserTier(user.isPremium);

    return user;
  }

  /**
   * Establish a Firebase session: optional extension-based sign-in (custom token or anonymous IDB sync), then anonymous sign-in.
   * @param skipExtensionSync - Skip all extension paths (used during logout)
   * @returns Firebase User
   */
  public async initFirebaseAnonymously(
    skipExtensionSync = false
  ): Promise<User> {
    try {
      if (!skipExtensionSync) {
        const extUser = await extensionUtils.askForExtensionUser();

        if (extUser?.isAnonymous && extUser.data?.length) {
          const currentUid = this.auth.currentUser?.uid;

          if (!currentUid || currentUid !== extUser.uid) {
            return await this.restoreSessionFromIdb(extUser.data[0]);
          }
        }
      }

      const userCredential = await signInAnonymously(this.auth);
      return userCredential.user;
    } catch (error) {
      logger.error('Failed to sign in anonymously', error, {
        tags: {
          feature: LOGGER_FEATURE.FIREBASE,
          action: LOGGER_ACTION.ANONYMOUS_LOGIN,
        },
      });
      throw error;
    }
  }

  /**
   * Update the current user's profile
   * @param displayName - User display name
   * @returns void
   */
  public async updateProfile(displayName: string): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      await updateProfile(user, { displayName });
    }
  }

  /**
   * Send a password reset email to the user
   * @param email - User email
   * @returns void
   */
  public async sendPasswordResetEmail(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }

  public getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  /**
   * Calls onChange whenever the server marks the user config as updated. The first
   * snapshot only records the current timestamp, since the app already loaded the config.
   */
  public subscribeToUserConfigChanges(
    uid: string,
    onChange: () => void
  ): () => void {
    let lastUpdate: unknown;
    let hasInitialSnapshot = false;
    return onSnapshot(
      doc(this.firestore, USER_CONFIG_SYNC_COLLECTION, uid),
      snapshot => {
        const nextLastUpdate = snapshot.data()?.lastUpdate;
        const isChanged = hasInitialSnapshot && nextLastUpdate !== lastUpdate;
        hasInitialSnapshot = true;
        lastUpdate = nextLastUpdate;
        if (isChanged) onChange();
      },
      error => {
        logger.error('Failed to listen to user config changes', error, {
          tags: {
            feature: LOGGER_FEATURE.FIREBASE,
            action: LOGGER_ACTION.SYNC_FROM_REMOTE,
          },
          extra: { userId: uid },
        });
      }
    );
  }

  public async storeUserConsentForAutoRenew(uid: string): Promise<void> {
    try {
      const consentRef = doc(
        collection(this.firestore, 'user_purchase_disclaimer_consent'),
        uid
      );
      await setDoc(consentRef, {
        consent: true,
        platform: 'web',
        userID: uid,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      logger.error('Failed to store user consent for auto-renew', error, {
        tags: {
          feature: LOGGER_FEATURE.CHECKOUT,
          action: LOGGER_ACTION.STORE_CONSENT,
        },
      });
    }
  }
}

export const firebaseUtils = new FirebaseUtils();
